import { useState, type FormEvent } from 'react';
import {
  Card, Badge, Button, AsyncBoundary, Drawer, Notice, useToast, ListPage,
} from '@kollez/design-system';
import {
  useQuestionBanks, useCreateQuestionBank, useBankQuestions,
  useApproveQuestion, useGenerateQuestions,
  type QuestionBankRow, type QuestionRow,
} from '@/lib/queries';

/**
 * /assess/bank — the question bank home for TPO / HoD / university_exam_controller.
 *
 * Layout:
 *   Left: list of QuestionBanks (empty → CTA to create).
 *   Right: selected bank's questions with per-row status badge, source badge (AI vs manual),
 *          approve action, and a "Nexie · Draft N more" drawer.
 */

const STATUS_TONE: Record<QuestionRow['status'], 'success' | 'warning' | 'info' | 'outline'> = {
  approved: 'success',
  draft: 'warning',
  review: 'info',
  retired: 'outline',
};

export function AssessBankPage() {
  const banks = useQuestionBanks();
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [newBankOpen, setNewBankOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const toast = useToast();

  // Auto-select first bank when data first loads
  const banksList = banks.data?.banks ?? [];
  const active = selectedBank ?? banksList[0]?._id ?? null;

  const questions = useBankQuestions(active ?? undefined);
  const approve = useApproveQuestion();

  async function handleApprove(id: string) {
    try {
      await approve.mutateAsync(id);
      toast.show({ title: 'Approved', message: 'Question is now live in this bank.' });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
        ?? 'Approve failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <div className="stack">
      {/* F-080: page-header always visible, even during load or empty state. */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Assessments</p>
          <h1 className="h-page">Question bank</h1>
          <p className="subhead">
            A bank per skill. Questions live here as drafts, get reviewed, become approved. Assessments draw from these.
          </p>
        </div>
        <div className="ph-actions">
          <Button variant="secondary" onClick={() => setNewBankOpen(true)}>+ New bank</Button>
          {active && (
            <Button variant="primary" onClick={() => setGenOpen(true)}>Nexie · Draft questions</Button>
          )}
        </div>
      </div>
      <AsyncBoundary
        kind="table"
        loading={banks.isLoading}
        error={banks.error}
        empty={!banks.isLoading && banksList.length === 0}
        emptyTitle="No question banks yet"
        emptyHint="Create a bank per skill or per drive type. Then let Nexie draft the first 20 questions."
        emptyAction={<Button variant="primary" size="sm" onClick={() => setNewBankOpen(true)}>+ New bank</Button>}
        onRetry={() => void banks.refetch()}
      >
      <div className="stack">

        <div className="grid gap-4 lg:grid-cols-[280px,1fr]">
          <Card padding="none">
            <div className="px-4 py-3 border-b border-line-tertiary text-xs uppercase tracking-wide text-ink-tertiary">
              Banks ({banksList.length})
            </div>
            <ul className="grid">
              {banksList.map((b) => {
                const isActive = b._id === active;
                return (
                  <li key={b._id}>
                    <button
                      type="button"
                      onClick={() => setSelectedBank(b._id)}
                      className={`w-full text-left px-4 py-3 border-b border-line-tertiary last:border-0 hover:bg-bg-secondary/40 ${
                        isActive ? 'bg-bg-secondary/60' : ''
                      }`}
                    >
                      <div className="text-sm font-medium truncate">{b.name}</div>
                      <div className="text-xs text-ink-tertiary flex justify-between mt-1">
                        <span>{b.categories.slice(0, 2).join(', ') || '—'}</span>
                        <span>{b.questionCount} Qs</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          <div>
            {active ? (
              <QuestionListForBank
                bankId={active}
                bank={banksList.find((b) => b._id === active)}
                onApprove={handleApprove}
                approving={approve.isPending}
              />
            ) : (
              <Card padding="lg">
                <p className="text-ink-tertiary">Pick a bank on the left to see its questions.</p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {newBankOpen && <NewBankDrawer onClose={() => setNewBankOpen(false)} />}
      {genOpen && active && <GenerateDrawer bankId={active} onClose={() => setGenOpen(false)} />}
      </AsyncBoundary>
    </div>
  );
}

function QuestionListForBank({
  bankId, bank, onApprove, approving,
}: {
  bankId: string;
  bank?: QuestionBankRow;
  onApprove: (id: string) => void;
  approving: boolean;
}) {
  const q = useBankQuestions(bankId);
  const rows = q.data?.questions ?? [];
  const drafted = rows.filter((r) => r.status === 'draft').length;
  const approved = rows.filter((r) => r.status === 'approved').length;

  return (
    <AsyncBoundary
      kind="table"
      loading={q.isLoading}
      error={q.error}
      empty={!q.isLoading && rows.length === 0}
      emptyTitle="No questions yet"
      emptyHint="Add manually or let Nexie draft the first 10 in one click."
      onRetry={() => void q.refetch()}
    >
      <ListPage<QuestionRow>
        eyebrow={bank?.name ?? '—'}
        title="Questions"
        kpis={[
          { label: 'Total', value: rows.length },
          { label: 'Drafts', value: drafted, tone: 'warning' },
          { label: 'Approved', value: approved, tone: 'success' },
          { label: 'AI-drafted', value: rows.filter((r) => r.source === 'ai').length, tone: 'info' },
        ]}
        segment={{
          key: 'status',
          options: [
            ['all', 'All'],
            ['draft', 'Drafts'],
            ['approved', 'Approved'],
            ['review', 'In review'],
          ],
        }}
        rows={rows}
        rowKey={(r) => r._id}
        columns={[
          {
            key: 'q',
            label: 'Question',
            render: (r) => (
              <div>
                <div className="cell-title line-clamp-2">{r.stem}</div>
                <div className="cell-sub">
                  <Badge tone="outline">{r.type}</Badge> · <Badge tone="outline">{r.difficulty}</Badge>
                  {r.skill && <> · {r.skill}</>}
                </div>
              </div>
            ),
          },
          {
            key: 'src',
            label: 'Source',
            hideMobile: true,
            render: (r) => <Badge tone={r.source === 'ai' ? 'info' : 'outline'}>{r.source}</Badge>,
          },
          {
            key: 'flags',
            label: 'Quality',
            hideMobile: true,
            render: (r) =>
              r.quality?.flags?.length ? (
                <span className="text-xs text-ink-tertiary">{r.quality.flags.length} checks</span>
              ) : (
                <span className="text-ink-tertiary text-xs">—</span>
              ),
          },
          {
            key: 'status',
            label: 'Status',
            right: true,
            render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>,
          },
        ]}
        drawer={(r) => ({
          eyebrow: `${r.type} · ${r.difficulty}`,
          title: r.stem,
          size: 'lg',
          body: (
            <div className="grid gap-3 text-sm">
              {r.options && r.options.length > 0 && (
                <div className="grid gap-2">
                  {r.options.map((o, i) => (
                    <div
                      key={i}
                      className={`p-2 border rounded ${o.correct ? 'border-ink-success bg-bg-success/10' : 'border-line-tertiary'}`}
                    >
                      {String.fromCharCode(65 + i)}. {o.text}
                      {o.correct && <span className="ml-2 text-ink-success text-xs">correct</span>}
                    </div>
                  ))}
                </div>
              )}
              {r.explanation && (
                <div className="p-3 bg-bg-info/10 rounded text-xs">
                  <b className="block mb-1">Why</b>
                  {r.explanation}
                </div>
              )}
              {r.quality?.flags?.length ? (
                <div className="p-3 bg-bg-secondary/40 rounded text-xs">
                  <b className="block mb-1">Nexie's quality checks</b>
                  <ul className="pl-4 list-disc">
                    {r.quality.flags.map((f, i) => <li key={i}>{f.replace(/-/g, ' ')}</li>)}
                  </ul>
                </div>
              ) : null}
              <div className="flex gap-2 pt-2 border-t border-line-tertiary">
                {r.status !== 'approved' && (
                  <Button variant="primary" size="sm" onClick={() => onApprove(r._id)} loading={approving}>
                    Approve for live use
                  </Button>
                )}
                <Button variant="secondary" size="sm">Edit</Button>
              </div>
            </div>
          ),
        })}
      />
    </AsyncBoundary>
  );
}

function NewBankDrawer({ onClose }: { onClose: () => void }) {
  const create = useCreateQuestionBank();
  const toast = useToast();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [cats, setCats] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        name,
        description: desc || undefined,
        categories: cats.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.show({ title: 'Bank created', message: 'Now add some questions or let Nexie draft them.' });
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
        ?? 'Create failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }
  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      title="New question bank"
      description="One bank per skill or drive type. You can rename it later."
      size="md"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>Create</Button>
        </div>
      }
    >
      <form onSubmit={submit} className="grid gap-4">
        <label className="grid gap-1.5">
          <span className="label text-sm">Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Aptitude — quant & LR" />
        </label>
        <label className="grid gap-1.5">
          <span className="label text-sm">Description (optional)</span>
          <textarea className="input" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
        </label>
        <label className="grid gap-1.5">
          <span className="label text-sm">Categories (comma-separated)</span>
          <input className="input" value={cats} onChange={(e) => setCats(e.target.value)} placeholder="aptitude, quant, LR" />
        </label>
      </form>
    </Drawer>
  );
}

function GenerateDrawer({ bankId, onClose }: { bankId: string; onClose: () => void }) {
  const gen = useGenerateQuestions();
  const toast = useToast();
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(10);
  const [types, setTypes] = useState<Array<'single' | 'multi' | 'tf' | 'blank' | 'num' | 'short' | 'long'>>(['single']);
  const [skill, setSkill] = useState('');
  const [mix, setMix] = useState({ easy: 30, medium: 50, hard: 20 });

  function toggleType(t: typeof types[number]) {
    setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  }

  async function submit() {
    if (topic.trim().length < 2) {
      toast.show({ tone: 'warning', message: 'Add a topic first.' });
      return;
    }
    if (types.length === 0) {
      toast.show({ tone: 'warning', message: 'Pick at least one type.' });
      return;
    }
    try {
      const r = await gen.mutateAsync({
        bankId, topic, count, types, skill: skill || undefined,
        levelMix: { easy: mix.easy / 100, medium: mix.medium / 100, hard: mix.hard / 100 },
      });
      toast.show({
        title: `Drafted ${r.draftedCount} questions`,
        message: 'Review each one, edit if needed, then approve.',
      });
      onClose();
    } catch (err) {
      toast.show({
        tone: 'warning',
        message: (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Generation failed',
      });
    }
  }

  return (
    <Drawer
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nexie · Draft questions"
      description="Nexie drafts them, you review. Nothing lands live without your approval."
      size="lg"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={gen.isPending}>
            {gen.isPending ? 'Drafting…' : `Draft ${count} question${count === 1 ? '' : 's'}`}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <Notice tone="info">
          Nexie writes each question, self-checks against 5 criteria (one correct answer, plausible distractors, plain language, level fits wording, no near-duplicates), and flags anything that failed the check.
        </Notice>
        <label className="grid gap-1.5">
          <span className="label text-sm">Topic</span>
          <input className="input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Time & Work, Trees, SQL joins, HTTP verbs" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label className="grid gap-1.5">
            <span className="label text-sm">Count</span>
            <input className="input" type="number" min={1} max={20} value={count} onChange={(e) => setCount(Number(e.target.value))} />
          </label>
          <label className="grid gap-1.5">
            <span className="label text-sm">Skill tag (optional)</span>
            <input className="input" value={skill} onChange={(e) => setSkill(e.target.value)} placeholder="dsa · sql · quant" />
          </label>
        </div>

        <div className="grid gap-1.5">
          <span className="label text-sm">Types</span>
          <div className="flex flex-wrap gap-2">
            {(['single', 'multi', 'tf', 'blank', 'num', 'short', 'long'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleType(t)}
                className={`tag ${types.includes(t) ? 'is-on' : ''}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-1.5">
          <span className="label text-sm">Level mix (%)</span>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <label>
              <span className="text-ink-tertiary text-xs">Easy</span>
              <input className="input" type="number" value={mix.easy} onChange={(e) => setMix({ ...mix, easy: Number(e.target.value) })} />
            </label>
            <label>
              <span className="text-ink-tertiary text-xs">Medium</span>
              <input className="input" type="number" value={mix.medium} onChange={(e) => setMix({ ...mix, medium: Number(e.target.value) })} />
            </label>
            <label>
              <span className="text-ink-tertiary text-xs">Hard</span>
              <input className="input" type="number" value={mix.hard} onChange={(e) => setMix({ ...mix, hard: Number(e.target.value) })} />
            </label>
          </div>
          <span className="text-xs text-ink-tertiary">
            Sum: {mix.easy + mix.medium + mix.hard}% {mix.easy + mix.medium + mix.hard !== 100 && '· should be 100'}
          </span>
        </div>
      </div>
    </Drawer>
  );
}
